import { useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowDown, MousePointer2 } from 'lucide-react';
import ParticleMark from '../components/ParticleMark';

// one headline line that slides up out of a mask
const Line = ({ d, children }) => (
  <span className="block overflow-hidden">
    <span className="hero-rise-line block pb-[0.06em]" style={{ '--d': `${d}ms` }}>
      {children}
    </span>
  </span>
);

export default function Hero() {
  const root = useRef(null);

  // the button leans toward the cursor
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference) and (hover: hover) and (pointer: fine)', () => {
        const el = root.current;
        const mags = el.querySelectorAll('[data-magnetic]');
        const onMove = (e) => {
          mags.forEach((m) => {
            const b = m.getBoundingClientRect();
            const dx = e.clientX - (b.left + b.width / 2);
            const dy = e.clientY - (b.top + b.height / 2);
            const near = Math.hypot(dx, dy) < 120;
            gsap.to(m, { x: near ? dx * 0.3 : 0, y: near ? dy * 0.3 : 0, duration: 0.5, ease: 'power3.out' });
          });
        };
        el.addEventListener('mousemove', onMove);
        return () => el.removeEventListener('mousemove', onMove);
      });
    },
    { scope: root }
  );

  const toWork = (e) => {
    const el = document.getElementById('work');
    if (!el) return;
    e.preventDefault();
    if (window.lenis) window.lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={root}
      id="home"
      aria-label="Farwings Tech Solution: your vision, our technology, real solutions"
      className="theme-dark relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-[#060E1B]"
    >
      {/* background: fine grid and slowly drifting glows */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_70%_60%_at_60%_40%,#000_20%,transparent_75%)]" />
        <div className="hero-blob absolute -left-40 top-10 h-[32rem] w-[32rem] bg-[#4FB0E7]/16" />
        <div className="hero-blob hero-blob-b absolute right-[-8rem] top-24 h-[36rem] w-[36rem] bg-[#4FB0E7]/12" />
      </div>

      <div className="frame grid flex-1 items-center gap-4 pb-16 pt-28 sm:pt-32 lg:grid-cols-12 lg:gap-8">
        {/* text */}
        <div className="order-2 lg:order-1 lg:col-span-7">

          <h1 className="display text-[clamp(2.4rem,12vw,3.4rem)] leading-[0.9] text-[#DCEBF2] sm:text-[5.5rem] lg:text-[6.4rem]">
            <Line d={100}>
              Your <span className="text-[#4FB0E7]">vision.</span>
            </Line>
            <Line d={260}>Our technology.</Line>
            <Line d={420}>
              <span className="hero-shimmer">Real solutions.</span>
            </Line>
          </h1>

          <p className="hero-rise lede mt-7" style={{ '--d': '650ms' }}>
            Websites, mobile apps, custom software and AI automation that bring you customers and save your team hours.
            Tell us the problem. You get a plan, timeline and price within 24 hours.
          </p>

          <div className="hero-rise mt-8" style={{ '--d': '780ms' }}>
            <a
              href="#work"
              onClick={toWork}
              data-magnetic
              className="btn inline-flex border border-white/20 bg-white/[0.04] uppercase tracking-[0.14em] text-white backdrop-blur-sm hover:border-[#4FB0E7]"
            >
              See our work <ArrowDown size={18} aria-hidden="true" />
            </a>
          </div>

        </div>

        {/* interactive particle bird */}
        <div className="hero-rise relative order-1 lg:order-2 lg:col-span-5" style={{ '--d': '200ms' }}>
          <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(79,176,231,0.22),transparent)]" />
          <div className="relative mx-auto h-[300px] max-w-[520px] sm:h-[420px] lg:h-[500px]">
            <ParticleMark />
          </div>
          <p className="mt-1 hidden items-center justify-center gap-2 text-xs text-white/70 sm:flex" aria-hidden="true">
            <MousePointer2 size={13} /> Move your cursor over the bird. Click to scatter it.
          </p>
        </div>
      </div>
    </section>
  );
}
